import { inject, injectable } from 'inversify';
import { TYPES } from '@/engine/ioc';

import type { ICustomCommissurePointsBoxController, IIndicationController } from '../interfaces.ts';
import type { BoxIndicationStateStore } from '../../common/BoxIndicationStateStore/BoxIndicationStateStore.ts';
import { type IReactionDisposer, reaction } from 'mobx';
import { PointIndicationPointerHandler as PointerHandler } from '../../indication-handler/PointIndicationPointerHandler.ts';
import type { IObjectViewModel, IViewPortManager } from '@/engine/app/interfaces';
import type { ViewController } from '@/engine/app/View.controller.ts';
import type { IHandlerDescriptor, IPointerHandler } from '@/components/viewPort/PointerController';
import type { ViewsService } from '@/engine/app/Views.service';
import { CustomCommissurePointsDescriptorResolver } from './CustomCommissurePointsDescriptorResolver.ts';

@injectable()
export class CustomCommissurePointsBoxController implements ICustomCommissurePointsBoxController {
  private readonly _descriptorResolver: CustomCommissurePointsDescriptorResolver;

  private readonly _viewsService: ViewsService;

  private readonly _viewController: ViewController;

  private readonly _indicationController: IIndicationController;

  private readonly _indicationStateStore: BoxIndicationStateStore;

  private _changeStateDisposer: IReactionDisposer | undefined;

  private readonly _indicatedCommissurePointLabel = 'Commissure';

  private _handlersDescriptors: Array<IHandlerDescriptor> = [];

  public constructor(
    @inject(TYPES.CustomCommissurePointsIndicationController) indicationController: IIndicationController,
    @inject(TYPES.CustomCommissurePointsDescriptorResolver) descriptorResolver: CustomCommissurePointsDescriptorResolver,
    @inject(TYPES.ViewsService) viewsService: ViewsService,
    @inject(TYPES.ViewController) viewController: ViewController,
    @inject(TYPES.BoxIndicationStateStore) indicationStateStore: BoxIndicationStateStore,
  ) {
    this._descriptorResolver = descriptorResolver;
    this._indicationController = indicationController;
    this._viewsService = viewsService;
    this._viewController = viewController;
    this._indicationStateStore = indicationStateStore;
    this._indicationController.setStateManager(this);
  }

  public init(): void {
    this._changeStateDisposer = reaction(
      () => this._indicationStateStore.isIndicating,
      // eslint-disable-next-line @typescript-eslint/no-misused-promises -- //TODO: [chebotarev] fix eslint no-misused-promises
      async (isIndicating: boolean): Promise<void> => {
        if (isIndicating) await this._startIndicating();
        else this._finishIndicating();
      }
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-misused-promises -- //TODO: [chebotarev] fix eslint no-misused-promises
  public async dispose(): Promise<void> {
    this._changeStateDisposer?.();
    if (this._indicationStateStore.isIndicating) {
      await this._indicationController.cancelIndication();
    }
    this._finishIndicating();
  }

  public get isIndicating(): boolean {
    return this._indicationStateStore.isIndicating;
  }

  public async cancelIndicating(): Promise<void> {
    await this._indicationController.cancelIndication();
  }

  public setIndicationState(isIndicating: boolean): void {
    this._indicationStateStore.setIsIndicating(isIndicating);
  }

  public setLabel(descriptor: string, label: string): void {
    this._viewsService.views.forEach((view) => {
      const vM: IObjectViewModel | undefined = view.getObjectByDescriptor(descriptor);
      vM?.setLabel?.(label);
    });
  }

  public resolveCommissurePointLabel(descriptor: string): string | undefined {
    if (descriptor.includes('as')) return 'Anteroseptal (AS)';
    if (descriptor.includes('ap')) return 'Anteroposterior (AP)';
    if (descriptor.includes('ps')) return 'Posteroseptal (PS)';
  }

  public async deleteCommissurePoint(descriptor: string): Promise<void> {
    await this._indicationController.removeIndicated(descriptor);
  }

  private async _startIndicating(): Promise<void> {
    const descriptor = this._descriptorResolver.resolveCommissurePointDescriptor();
    const pointNumber = this._descriptorResolver.getCommissurePointNumber(descriptor);

    this._viewController.getRegisteredViewPorts().forEach((vpm: IViewPortManager): void => {
      const handler: IPointerHandler = new PointerHandler(vpm, this._indicationController);
      const handlerDescriptor: IHandlerDescriptor = vpm.addPointerHandler(handler);
      this._handlersDescriptors.push(handlerDescriptor);
    });

    await this._indicationController.initializeIndication(descriptor, this._indicatedCommissurePointLabel, pointNumber);
  }

  private _finishIndicating(): void {
    this._handlersDescriptors.forEach((descriptor: IHandlerDescriptor) => descriptor.remove());
    this._handlersDescriptors = [];
  }
}
